import './FailReportFilter.scss';

import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useForm } from '@refinedev/antd';
import { Button, Form, Popover } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FullscreenDrawer from '@/components/general/Drawers/FullscreenDrawer';
import { useUiContext } from '@/contexts/UiContext';
import { FailReportWarrantyString } from '@/model/FailReport/FailReport';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';

import { useFailReportFilter } from './useFailReportFilter';

interface FailReportFilterProps {
  filters: any[];
  setFilters: Function;
  setCurrent: Function;
}

const FailReportFilter = (props: FailReportFilterProps) => {
  const { t } = useTranslation();
  const { isMobile } = useUiContext();
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const { formProps } = useForm<Shipment>({ warnWhenUnsavedChanges: false });
  const { form } = formProps;
  const { filters, setFilters, setCurrent } = props;
  const { usersSelectProps, categorySelectProps, subcategorySelectProps, ticketsSelectProps } =
    useFailReportFilter(formProps, filters);
  const handleClearFilters = useCallback(() => {
    form?.resetFields();
    setFilters([], 'replace');
    setCurrent(1);
    setFilterPopoverOpen(false);
  }, [form, setCurrent, setFilters]);
  const applyFilters = useCallback(
    values => {
      const filters: any[] = [];

      // Ticket
      if (values.ticket) {
        filters.push({
          field: 'ticket',
          operator: 'eq',
          value: values.ticket,
        });
      }

      if (values.category) {
        filters.push({
          field: 'category',
          operator: 'eq',
          value: values.category,
        });
      }

      if (values.subcategory) {
        filters.push({
          field: 'subcategory',
          operator: 'eq',
          value: values.subcategory,
        });
      }

      // Creado por
      if (values.review_by) {
        filters.push({
          field: 'review_by',
          operator: 'eq',
          value: values.review_by,
        });
      }

      // En garantía
      if (values.warranty != null) {
        filters.push({
          field: 'warranty',
          operator: 'eq',
          value: values.warranty,
        });
      }

      setFilters(filters, 'replace');
      setCurrent(1);
      setFilterPopoverOpen(false);
    },
    [setFilters, setFilterPopoverOpen, setCurrent],
  );
  const filterContent = useMemo(
    () => (
      <Form
        labelAlign="left"
        {...formProps}
        style={{ marginBottom: 8 }}
        onFinish={applyFilters}
        className="FailReportFilterPopover">
        <div className="FailReportFilterPopoverRows">
          <Form.Item label={t('Ticket')} name={'ticket'} className="FailReportFilterRow">
            <AntdSelect {...ticketsSelectProps} allowClear showSearch />
          </Form.Item>
          <Form.Item label={t('Categoría')} name={'category'} className="FailReportFilterRow">
            <AntdSelect {...categorySelectProps} allowClear showSearch />
          </Form.Item>
          <Form.Item label={t('Subcategoría')} name={'subcategory'} className="FailReportFilterRow">
            <AntdSelect {...subcategorySelectProps} allowClear showSearch />
          </Form.Item>

          <Form.Item label={t('Revisado por')} name={'review_by'} className="FailReportFilterRow">
            <AntdSelect options={usersSelectProps.options} allowClear showSearch />
          </Form.Item>
          <Form.Item label={t('En garantía')} name={'warranty'} className="FailReportFilterRow">
            <AntdSelect
              options={Object.keys(FailReportWarrantyString).map(v => ({
                label: FailReportWarrantyString[v],
                value: v,
              }))}
              allowClear
            />
          </Form.Item>
        </div>

        <div className="FailReportFilterActions">
          <Button type="default" style={{ marginLeft: 8 }} onClick={() => handleClearFilters()}>
            {t('Limpiar')}
          </Button>
          <Button htmlType="submit" type="primary">
            {t('Buscar')}
          </Button>
        </div>
      </Form>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketsSelectProps, usersSelectProps, categorySelectProps, subcategorySelectProps],
  );

  return (
    <div className="FailReportFilter">
      {isMobile ? (
        <>
          <Button
            className={filters.length > 0 ? 'FailReportFilterApplied' : ''}
            onClick={() => setFilterPopoverOpen(true)}
            icon={<FilterOutlined />}
          />
          <FullscreenDrawer
            disablePush
            title={t('Filtros')}
            open={filterPopoverOpen}
            setOpen={setFilterPopoverOpen}>
            {filterContent}
          </FullscreenDrawer>
        </>
      ) : (
        <Popover
          openClassName="FailReportFilterPopover"
          content={filterContent}
          title={t('Filtros')}
          trigger="click"
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          placement="bottomRight">
          <Button
            className={filters.length > 0 ? 'FailReportFilterApplied' : ''}
            icon={filters.length > 0 ? <FilterFilled /> : <FilterOutlined />}>
            {t('Filtrar')}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default FailReportFilter;
